import React, { useState, useEffect } from "react";
import Button from "../components/Button";
import Cta from "../components/Cta";
import Seo from "../components/Seo";
import AOS from "aos";
import "aos/dist/aos.css";

export default function SimpleSolarCalculator() {
  const [monthlyBill, setMonthlyBill] = useState(2500);
  const [tariff, setTariff] = useState(8);
  const [offset, setOffset] = useState(100);
  const [results, setResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const [psh] = useState(5.6); // Peak Sun Hours
  const [lossFactor] = useState(0.85); // Loss Factor

  useEffect(() => {
    AOS.init({
      duration: 1000,
      easing: "ease-in-out",
      once: true,
      mirror: false
    });
  }, []);

  const fmt = (n) =>
    n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  const fmtRs = (n) =>
    n.toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    });

  const calculateResults = () => {
    setIsCalculating(true);
    
    // Simulate calculation delay for better UX
    setTimeout(() => {
      const monthlyKWh = monthlyBill / tariff;
      const dailyKWh = monthlyKWh / 30;
      const targetDailyKWh = (dailyKWh * offset) / 100;

      const systemKW = targetDailyKWh / (psh * lossFactor);
      const annualKWh = systemKW * psh * 365 * lossFactor;
      const annualSavings = annualKWh * tariff;

      setResults({
        systemKW: fmt(systemKW),
        annualKWh: fmt(annualKWh),
        annualSavings: fmtRs(annualSavings),
      });
      setIsCalculating(false);
    }, 800);
  };

  const steps = [
    {
      icon: "💡",
      title: "Enter Monthly Bill",
      desc: "Provide your electricity bill to calculate usage.",
    },
    {
      icon: "⚡",
      title: "Set Tariff",
      desc: "Input your cost per unit (kWh) of electricity.",
    },
    {
      icon: "🎯",
      title: "Choose Offset",
      desc: "Decide what percentage of electricity you want from solar.",
    },
    {
      icon: "📊",
      title: "Get Results",
      desc: "See system size, annual generation, and savings.",
    },
  ];

  return (
    <>
      <Seo
        title="Solar Savings Calculator | Estimate Your Solar System"
        description="Use the KK Solar calculator to estimate your rooftop solar system size and annual savings in Kutch, Gujarat."
        url="https://kksolar.co.in/solar-calculator"
      />
      <div className="max-w-8xl mt-24 mx-auto overflow-hidden">    
      {/* Hero Section */}
      <section className="relative min-h-[60vh] bg-gradient-to-br from-primary via-secondary to-blue-900 flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-opprimary/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-40 h-40 bg-white/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>
        
        <div 
          className="relative text-center max-w-4xl px-6 space-y-6"
          data-aos="fade-up"
          data-aos-delay="200"
        >
          <div 
            className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-white/20"
            data-aos="fade-down"
            data-aos-delay="400"
          >
            <span className="w-3 h-3 bg-opprimary rounded-full mr-3 animate-pulse"></span>
            <span className="text-white font-semibold text-lg">Solar Savings Calculator</span>
          </div>
          
          <h1 
            className="text-5xl lg:text-6xl font-bold text-white leading-tight"
            data-aos="fade-up"
            data-aos-delay="300"
          >
            Estimate Your <span className="text-opprimary">Solar Savings</span>
          </h1>
          
          <p 
            className="text-xl opacity-90 max-w-3xl mx-auto leading-relaxed text-white"
            data-aos="fade-up"
            data-aos-delay="400"
          >
            Calculate your potential electricity savings and system size easily. 
            Discover how much you can save with solar energy.
          </p>
        </div>

      
      </section>

      {/* Calculator Section */}
      <section className="py-20 bg-background relative overflow-hidden">
        {/* Background Animation */}
        <div className="absolute inset-0 bg-gradient-to-br from-opprimary/5 to-primary/5 animate-gradient-x"></div>
        
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div 
            className="bg-white rounded-2xl shadow-2xl overflow-hidden transform hover:shadow-3xl transition-all duration-500"
            data-aos="zoom-in"
            data-aos-delay="200"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-opprimary to-orange-500 p-8 text-center">
              <div className="text-4xl mb-4">☀️</div>
              <h1 className="text-3xl font-bold text-white drop-shadow-md">
                Solar Bill Saver
              </h1>
              <p className="text-white/90 text-lg mt-2">
                Estimate your savings with solar energy
              </p>
            </div>

            {/* Calculator Content */}
            <div className="p-8">
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {/* Monthly Bill Input */}
                <div 
                  className="space-y-3 group"
                  data-aos="fade-right"
                  data-aos-delay="300"
                >
                  <label className="block">
                    <span className="text-sm font-semibold text-primary flex items-center gap-2">
                      <span className="w-2 h-2 bg-opprimary rounded-full"></span>
                      Monthly Bill (₹)
                    </span>
                    <input
                      type="number"
                      value={monthlyBill}
                      onChange={(e) => setMonthlyBill(Number(e.target.value))}
                      className="w-full mt-2 p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-opprimary focus:border-opprimary outline-none transition-all duration-300 group-hover:border-opprimary/50"
                    />
                  </label>
                  <div className="text-xs text-gray-500">
                    Your average monthly electricity bill
                  </div>
                </div>

                {/* Tariff Input */}
                <div 
                  className="space-y-3 group"
                  data-aos="fade-up"
                  data-aos-delay="400"
                >
                  <label className="block">
                    <span className="text-sm font-semibold text-primary flex items-center gap-2">
                      <span className="w-2 h-2 bg-opprimary rounded-full"></span>
                      Tariff (₹/kWh)
                    </span>
                    <input
                      type="number"
                      value={tariff}
                      onChange={(e) => setTariff(Number(e.target.value))}
                      className="w-full mt-2 p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-opprimary focus:border-opprimary outline-none transition-all duration-300 group-hover:border-opprimary/50"
                    />
                  </label>
                  <div className="text-xs text-gray-500">
                    Cost per unit of electricity
                  </div>
                </div>

                {/* Offset Input */}
                <div 
                  className="space-y-3 group"
                  data-aos="fade-left"
                  data-aos-delay="500"
                >
                  <label className="block">
                    <span className="text-sm font-semibold text-primary flex items-center gap-2">
                      <span className="w-2 h-2 bg-opprimary rounded-full"></span>
                      Offset Target (%)
                    </span>
                    <input
                      type="number"
                      value={offset}
                      onChange={(e) => setOffset(Number(e.target.value))}
                      className="w-full mt-2 p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-opprimary focus:border-opprimary outline-none transition-all duration-300 group-hover:border-opprimary/50"
                    />
                  </label>
                  <div className="text-xs text-gray-500">
                    Percentage of bill to offset with solar
                  </div>
                </div>
              </div>

              {/* Calculate Button */}
              <div 
                className="text-center"
                data-aos="fade-up"
                data-aos-delay="600"
              >
                <Button
                  onClick={calculateResults}
                  variant="solidorange"
                  className="text-lg px-12 py-4 relative overflow-hidden"
                  disabled={isCalculating}
                >
                  {isCalculating ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Calculating...
                    </span>
                  ) : (
                    "Calculate Savings"
                  )}
                </Button>
              </div>

              {/* Results */}
              {results && (
                <div 
                  className="mt-8 bg-gradient-to-br from-opprimary/10 to-primary/10 p-8 rounded-2xl border-2 border-opprimary/20 transform transition-all duration-500"
                  data-aos="fade-up"
                  data-aos-delay="700"
                >
                  <h2 className="text-2xl font-bold text-primary mb-6 text-center flex items-center justify-center gap-3">
                    <span className="text-opprimary">📊</span>
                    Your Solar Potential
                  </h2>
                  
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center group transform hover:scale-105 transition-transform duration-300">
                      <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                        <div className="text-3xl text-opprimary mb-3">⚡</div>
                        <div className="text-2xl font-bold text-primary mb-2">{results.systemKW} kW</div>
                        <div className="text-sm text-gray-600">System Size Needed</div>
                      </div>
                    </div>
                    
                    <div className="text-center group transform hover:scale-105 transition-transform duration-300">
                      <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                        <div className="text-3xl text-opprimary mb-3">🌞</div>
                        <div className="text-2xl font-bold text-primary mb-2">{results.annualKWh} kWh</div>
                        <div className="text-sm text-gray-600">Annual Generation</div>
                      </div>
                    </div>
                    
                    <div className="text-center group transform hover:scale-105 transition-transform duration-300">
                      <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                        <div className="text-3xl text-opprimary mb-3">💰</div>
                        <div className="text-2xl font-bold text-primary mb-2">{results.annualSavings}</div>
                        <div className="text-sm text-gray-600">Annual Savings</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 text-center">
                    <p className="text-sm text-gray-600">
                      *Based on {psh} peak sun hours and {lossFactor * 100}% system efficiency
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        
        <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
          <div 
            className="mb-16"
            data-aos="fade-up"
          >
            <div className="inline-flex items-center px-4 py-2 bg-opprimary/10 rounded-full border border-opprimary/20 mb-6">
              <span className="text-opprimary font-semibold text-sm">Simple Process</span>
            </div>
            <h2 className="text-4xl font-bold text-primary mb-4">How It Works</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Four simple steps to calculate your solar potential and start saving
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div
                key={index}
                data-aos="flip-up"
                data-aos-delay={index * 200}
                className="group"
              >
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl p-8 flex flex-col items-center text-center transform hover:-translate-y-4 transition-all duration-500 border border-gray-100 relative overflow-hidden">
                  {/* Step Number */}
                  <div className="absolute top-4 right-4 text-6xl font-bold text-gray-100">
                    {index + 1}
                  </div>
                  
                  {/* Icon */}
                  <div className="text-5xl mb-6 transform group-hover:scale-110 group-hover:rotate-12 transition-transform duration-300">
                    {step.icon}
                  </div>
                  
                  <h3 className="font-semibold text-xl text-primary mb-4 group-hover:text-secondary transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{step.desc}</p>
                  
                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-opprimary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
       <Cta />

      <style jsx>{`
        .bg-grid-pattern {
          background-image: linear-gradient(rgba(2, 26, 92, 0.1) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(2, 26, 92, 0.1) 1px, transparent 1px);
          background-size: 50px 50px;
        }
      `}</style>
    </div>
    </>
  );
}
